import { useEffect, useState } from "react"
import { PasswordInput } from "./PasswordInput"
import { PrimaryButton } from "../buttons/PrimaryButton"
import { useSearchParams } from "react-router-dom"
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import { Label } from "./Label"
import { SpinnerContainer } from "../common/SpinnerContainer"
import { passwordResetConfirm } from "../../api/requests/auth"
import { ResetPasswordTokenModel } from "../../api/requests/interfaces/Auth"
import { jwtDecode } from "jwt-decode"
import { isBefore } from "date-fns"
import CheckIcon from '@mui/icons-material/Check';

export const ResetPasswordForm: React.FC = () => {
    const [password, setPassword] = useState("");
    const [passwordCopy, setPasswordCopy] = useState("");
    const [passwordsMatch, setPasswordsMatch] = useState(false);
    const [passwordValid, setPasswordValid] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isErrorRequest, setIsErrorRequest] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [pwdResetToken, setPwdResetToken] = useState<ResetPasswordTokenModel|undefined>(undefined);
    const [pwdResetTokenString, setPwdResetTokenString] = useState("");
    const [resetTokenValid, setResetTokenValid] = useState(true);
    const [resetSuccess, setResetSuccess] = useState(false);
    
    useEffect(() => {
        const passwordResetToken = searchParams.get('token');
        if (passwordResetToken) {
            const decodedToken = jwtDecode<ResetPasswordTokenModel>(passwordResetToken)
            setPwdResetToken(decodedToken);
            if (isBefore(decodedToken.exp * 1000, new Date())) {
                setResetTokenValid(false);
            }
            setPwdResetTokenString(passwordResetToken);
        }
    }, [searchParams]);

    useEffect(() => {
        let inter: undefined | NodeJS.Timer;

        if (pwdResetToken) {
            inter = setInterval(() => {
                if (isBefore(pwdResetToken.exp * 1000, new Date())) {
                    setResetTokenValid(false);
                }
            }, 1000)
        }

        return () => {
            if (inter) {
                clearInterval(inter);
            }
        }
    }, [pwdResetToken])

    function isValidPassword(password: string): boolean {
        const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/;
        return passwordRegex.test(password);
    }

    const handlePassWordChange = (val: string) => {
        setPassword(val);
        setPasswordValid(isValidPassword(val));
    }

    const handlePassWordCopyChange = (val: string) => {
        setPasswordCopy(val);
        if (val != password) {
            setPasswordsMatch(false);
        } else {
            setPasswordsMatch(true)
        }
    }

    const checkFieldValids = (): boolean => {
        return passwordValid && passwordsMatch && (pwdResetToken !== undefined) && resetTokenValid
    }

    const handleResetPassword = async () => {
        setIsSubmitted(true);
        if (!checkFieldValids()) {
            return;
        }
        setLoading(true);
        try {
            setIsErrorRequest(false);
            const res = await passwordResetConfirm({pwd1: password, pwd2: passwordCopy}, pwdResetTokenString);
            setResetSuccess(true);
            setLoading(false);
        } catch (e) {
            setIsErrorRequest(true);
            setLoading(false);
        }
    }

    return (
        <div>
            <SpinnerContainer show={loading}>
                <div className="w-full md:w-[400px] space-y-2 h-fit shadow-main z-10 bg-white px-8 py-7 rounded-xl">
                    <h2>Parooli taastamine</h2>
                    {
                        !resetSuccess ?
                        <>
                        {
                            resetTokenValid ? 
                            <>
                                <form onSubmit={(e) => { e.preventDefault() }} className="space-y-2 flex flex-col gap-2">
                                
                                <div>
                                    <div className="flex row align-middle gap-1">
                                        <Label value="Uus parool" />
                                        <Tooltip title="Parool peab olema pikkusega 8 tähemärki ja sisaldama 1 suurt tähte, 1 numbrit, 1 järgnevatest sümbolitest @$!%*?&#" arrow>
                                            <InfoIcon className="cursor-pointer text-blue-500" style={{ width: '18px', height: '18px' }} />
                                        </Tooltip>
                                    </div>
                                    <PasswordInput value={password} onChange={(e) => handlePassWordChange(e)} placeHolder="Parool" invalid={!passwordValid && isSubmitted}
                                        errorText="Parool ei vasta nõuetele. Parool peab olema pikkusega 8 tähemärki ja sisaldama 1 suurt tähte, 1 numbrit, 1 järgnevatest sümbolitest @$!%*?&#"></PasswordInput>
                                </div>
                                <div>
                                    <div className="flex row align-middle">
                                        <Label value="Parool uuesti" />
                                    </div>
                                    <PasswordInput value={passwordCopy} onChange={(e) => handlePassWordCopyChange(e)} placeHolder="Parool uuesti"
                                        invalid={!passwordsMatch && password.length > 0 && isSubmitted}
                                        errorText="Parool ei ühti esialgse parooliga"></PasswordInput>
                                </div>
                                {/* <div className="flex">
                                    <div className="flex items-center gap-1"><Checkbox checked={true} onChange={(v) => {}}></Checkbox> Jäta mind meelde</div>
                                </div> */}
                                <div className="pt-4">
                                    <PrimaryButton title='Muuda' onClick={handleResetPassword} />
                                </div>
                            </form>
                            {
                                isErrorRequest ?
                                    <div className="text-red-500">Parooli muutmisel esines viga! Võta ühendust klienditoega info@rentic.ee</div>
                                    :
                                    <></>
                            }
                            </>
                            :
                            <div className="text-orange-500">Parooli taastamise võti vigane või kehtetu. Alustage uuesti parooli muutmist</div>
                        }
                        </>
                        :
                        <div className="text-lg font">
                            <CheckIcon style={{width: "30px", height: "30px"}} className="text-green-500"/>
                            <div>Parool edukalt muudetud, saate sisse logida.</div>
                        </div>
                    }
                </div>
            </SpinnerContainer>
        </div>
    )
}